import React, { useState, useMemo } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DataTable from './DataTable'; // Notice it needs to import from components, wait, TablePage imports it from `../components/DataTable`. Let's import correctly.
import HolidayCalendarGrid from '../components/HolidayCalendarGrid';

export default function HolidayCalendarPage({ 
  records, schema, tableName, canEditRecord, canDeleteRecord, handleEdit, handleDelete, handleView, canAddRecord, handleAdd
}) {
  const [viewMode, setViewMode] = useState('calendar');
  const [listMode, setListMode] = useState('upcoming');

  const { upcomingHolidays, pastHolidays } = useMemo(() => {
    const upcoming = [];
    const past = [];
    const todayStr = new Date().toISOString().split('T')[0];
    const currentYear = new Date().getFullYear();

    (records || []).forEach(r => {
      if (!r.holiday_date) return;
      
      if (r.recurring_flag === 'Y') {
        const md = r.holiday_date.substring(5);
        const thisYearStr = `${currentYear}-${md}`;
        
        if (thisYearStr >= todayStr) {
          upcoming.push({ ...r, holiday_date: thisYearStr });
          past.push({ ...r, holiday_date: `${currentYear - 1}-${md}` });
        } else {
          past.push({ ...r, holiday_date: thisYearStr });
          upcoming.push({ ...r, holiday_date: `${currentYear + 1}-${md}` });
        }
      } else {
        if (r.holiday_date >= todayStr) upcoming.push(r);
        else past.push(r);
      }
    });

    upcoming.sort((a,b) => a.holiday_date.localeCompare(b.holiday_date));
    past.sort((a,b) => b.holiday_date.localeCompare(a.holiday_date));
    
    return { upcomingHolidays: upcoming, pastHolidays: past };
  }, [records]);

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {viewMode === 'list' && (
          <ToggleButtonGroup
            value={listMode}
            exclusive
            onChange={(e, newMode) => { if (newMode) setListMode(newMode); }}
            size="small"
            sx={{ bgcolor: 'white' }}
          >
            <ToggleButton value="upcoming" sx={{ textTransform: 'none', px: 2, fontWeight: 500 }}>
              Upcoming ({upcomingHolidays.length})
            </ToggleButton>
            <ToggleButton value="past" sx={{ textTransform: 'none', px: 2, fontWeight: 500 }}>
              Past ({pastHolidays.length})
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
          size="small"
          sx={{ bgcolor: 'white' }}
        >
          <ToggleButton value="calendar" sx={{ textTransform: 'none', px: 2 }}>
            <CalendarMonthIcon sx={{ mr: 1, fontSize: 18 }} />
            Calendar View
          </ToggleButton>
          <ToggleButton value="list" sx={{ textTransform: 'none', px: 2 }}>
            <ViewListIcon sx={{ mr: 1, fontSize: 18 }} />
            List View
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'calendar' ? (
        <HolidayCalendarGrid
          records={records}
          onAdd={handleAdd}
          onEdit={canEditRecord ? handleEdit : undefined}
        />
      ) : (
        <DataTable
          records={listMode === 'upcoming' ? upcomingHolidays : pastHolidays}
          schema={schema}
          tableName={tableName}
          onEdit={canEditRecord ? handleEdit : undefined}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onView={handleView}
          canCreate={canAddRecord}
        />
      )}
    </Box>
  );
}
